import { useState, useEffect } from 'react';
import { SetFormValues } from '@type/form-types';
import { MirrorData, SetMirrorData } from '@type/job-add';

/** Props */
interface UseJobVacanciesProps {
  setFormValues: SetFormValues,
  reqMirrorData?: Pick<MirrorData, 'vacancies' | 'addresses'>,
  setMirrorData?: SetMirrorData,
  canAutoChangeVacancies?: boolean,
}

/**
 * @description Use job vacancies hook. Increase or decrease vacancies based on the number of addresses
 * as long as the auto change vacancies is enabled.
 * - `setFormValues` - function to update the form state
 * - `reqMirrorData`: The required form mirror data.
 * - `setMirrorData` - function to update the mirror form state
 * - `canAutoChangeVacancies` - if we can auto change vacancies
 */
export const useJobVacancies = (props: UseJobVacanciesProps) => {
  // Destructure props
  const {
    setFormValues,
    reqMirrorData,
    setMirrorData,
    canAutoChangeVacancies,
  } = props;

  // State for auto changing vacancies
  const [autoChangeVacancies, setAutoChangeVacancies] = useState<boolean>(Boolean(canAutoChangeVacancies));

  // Set auto change vacancies
  // **************************
  useEffect(() => {
    if (autoChangeVacancies && reqMirrorData?.vacancies && reqMirrorData?.addresses) {
      const vacanciesNr = Number(reqMirrorData.vacancies);
      const addressesNr = reqMirrorData.addresses?.length || 0;

      // Increase or decrease vacancies based on the number of addresses
      if (vacanciesNr > 0 && addressesNr > 0 && vacanciesNr !== addressesNr) {
        setFormValues({ vacancies: addressesNr });
        setMirrorData?.({ vacancies: addressesNr }, false, true);
      }
    }
  }, [autoChangeVacancies, reqMirrorData?.vacancies, reqMirrorData?.addresses, setFormValues, setMirrorData]);


  // Return
  // **************************
  return {
    autoChangeVacancies,
    setAutoChangeVacancies,
  };
};
